import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import authStore from "../../stores/AuthStore";
import { BarStatus, User, UserRoles } from "../../stores/AuthStore/types";
import {
  Controller,
  useForm,
  UseFormSetValue,
  UseFormTrigger,
} from "react-hook-form";
import profileFormRules from "./rules";
import useTranslation from "../../hooks/useTranslation";
import { barList } from "./helper";
import feedbackStore from "../../stores/FeedbackStore";
import { FeedbackSeverity } from "../../stores/FeedbackStore/types";
// import { Document, DocumentType } from "../../components/Documents/types";
// import Documents from "../../components/Documents";
// import documentStore from "../../stores/DocumentStore";
import { cityMap } from "../../utils/cityCodes";
import LawyerAgreementDialog from "./LawyerAgreementDialog";

interface LawyerFieldsProps {
  user?: User;
  parentForm?: {
    trigger?: UseFormTrigger<User>;
    setValue?: UseFormSetValue<User>;
  };
}

export const LawyerFields = ({ user, parentForm }: LawyerFieldsProps) => {
  const { t } = useTranslation();
  const isFormEnabled =
    authStore.user?.barStatus === BarStatus.DEACTIVE ||
    authStore.user?.barStatus === BarStatus.REJECTED;
  // const [photosLength, setPhotosLength] = useState(0);
  const [hasSubmit, setHasSubmit] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [data, setData] = useState<User>();
  const {
    control,
    formState: { errors },
    handleSubmit,
    getValues,
    setValue,
    trigger,
  } = useForm<User>({
    defaultValues: {
      barName: "",
    },
  });
  const isFormFilled =
    getValues("barNo") && getValues("barName"); /*&& photosLength === 2*/

  useEffect(() => {
    setValue("barNo", user?.barNo || authStore.user.barNo);
    setValue("barName", user?.barName || authStore.user.barName);
  }, [setValue]);

  /*
  const uploadDocument = async (
    document: Document
  ): Promise<void | Document> => {
    return documentStore.uploadDocument(document).catch((error) => {
      console.error(error);
      feedbackStore.setShowFeedback(true, {
        message: "doc-save-failed",
        severity: FeedbackSeverity.ERROR,
      });
    });
  };
  */

  const updateUser = (updatedUser: User) => {
    authStore
      .updateUser({ ...user, ...updatedUser })
      .then(() => {
        authStore.user = {
          ...authStore.user,
          barStatus: BarStatus.PENDING,
        };

        feedbackStore.setShowFeedback(true, {
          severity: FeedbackSeverity.SUCCESS,
          message: "lawyer-application-success",
        });
      })
      .catch(() => {
        feedbackStore.setShowFeedback(true, {
          severity: FeedbackSeverity.ERROR,
          message: "error",
        });
      })
      .finally(() => setHasSubmit(false));
  };

  const onSubmit = (data: User) => {
    setData(data);
    setDialogOpen(true);
  };

  const onAgreementAccept = () => {
    setDialogOpen(false);

    if (data) {
      setHasSubmit(true);
      updateUser(data);

      /*
      const unsavedDocuments = documentStore.documents.filter(
        (doc) => doc.documentType === DocumentType.IdPhoto && !doc.id
      );

      if (unsavedDocuments && unsavedDocuments.length > 0) {
        uploadDocument(unsavedDocuments[0])
          .then(() => uploadDocument(unsavedDocuments[1]))
          .then(() => {
            updateUser(data);
          });
      } else {
        updateUser(data);
      }
      */
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Card className="lawyerCard" sx={{ width: { xs: "100%", sm: "360px" } }}>
        <CardContent>
          <header>
            {!authStore.userHasRole(UserRoles.ADMIN) && (
              <>
                <h4 style={{ marginTop: 0 }}>{t("lawyer-application")}</h4>
                {authStore.user?.barStatus === BarStatus.DEACTIVE ? (
                  <p>{t("lawyer-registration-info")}</p>
                ) : authStore.user?.barStatus === BarStatus.ACTIVE ? (
                  <Alert severity={"success"}>
                    {t("lawyer-bar-info")}
                    <p>
                      {t("bar-no")}: {authStore.user.barNo}
                    </p>
                    <p>
                      {t("bar-name")}:&nbsp;
                      {cityMap[authStore.user.barName?.toString() || ""]}
                    </p>
                  </Alert>
                ) : authStore.user?.barStatus === BarStatus.REJECTED ? (
                  <Alert severity={"error"}>
                    {t("rejected")}: {authStore.user.reason}
                  </Alert>
                ) : (
                  authStore.user?.barStatus === BarStatus.PENDING && (
                    <Alert severity={"info"}>{t("lawyer-pending-info")}</Alert>
                  )
                )}
              </>
            )}
          </header>
          {isFormEnabled && (
            <>
              <Controller
                name={"barNo"}
                control={control}
                disabled={!isFormEnabled}
                rules={profileFormRules.barNo}
                render={({ field: { onChange, value, disabled } }) => (
                  <>
                    <h4>{t("bar-no")}:</h4>
                    <TextField
                      error={!!errors.barNo}
                      placeholder={t("please-write-here")}
                      rows={1}
                      size={"small"}
                      disabled={disabled}
                      onChange={(e) => {
                        onChange(e);
                        trigger("barNo").then();
                        trigger("barName").then();
                        parentForm?.setValue?.(
                          "barNo",
                          parseInt(e.target.value)
                        );
                        parentForm?.trigger?.("roles").then();
                      }}
                      value={value}
                      type={"number"}
                    />
                    <div className="error mt">
                      {errors.barNo && t(`bar-no-${errors.barNo.type}`)}
                    </div>
                  </>
                )}
              />
              <Controller
                name={"barName"}
                control={control}
                disabled={!isFormEnabled}
                rules={profileFormRules.barName}
                render={({ field: { onChange, value, disabled } }) => (
                  <>
                    <h4>{t("bar-name")}:</h4>
                    <FormControl>
                      <Select
                        error={!!errors.barName}
                        placeholder={t("please-write-here")}
                        size={"small"}
                        disabled={disabled}
                        onChange={(e) => {
                          onChange(e);
                          trigger("barNo").then();
                          trigger("barName").then();
                          parentForm?.setValue?.("barName", e.target.value);
                          parentForm?.trigger?.("roles").then();
                        }}
                        value={value}
                      >
                        {barList.map((bar) => (
                          <MenuItem key={bar.value} value={bar.value}>
                            {bar.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <div className="error mt">
                      {errors.barName && t(`bar-name-${errors.barName.type}`)}
                    </div>
                  </>
                )}
              />
            </>
          )}
          {/* Document upload is disabled until next implementation
          isFormEnabled && !authStore.userHasRole(UserRoles.ADMIN) && (
            <>
              <Documents
                inputId="lawyerIdDocuments"
                fileFormats={["image/jpg", "image/jpeg"]}
                fileLimit={2}
                documentType={DocumentType.IdPhoto}
                onDocumentAdded={(documents) =>
                  setPhotosLength(documents.length)
                }
                onDocumentRemoved={(documents) =>
                  setPhotosLength(documents.length)
                }
              />
            </>
          )*/}
        </CardContent>
        {isFormEnabled && !authStore.userHasRole(UserRoles.ADMIN) && (
          <CardActions className="formActions">
            <Button
              type="submit"
              variant={"contained"}
              disabled={
                !isFormFilled ||
                hasSubmit ||
                Object.values(errors).some((error) => error)
              }
            >
              {hasSubmit ? (
                <CircularProgress size={"16px"} />
              ) : (
                t("lawyer-sign-up")
              )}
            </Button>
          </CardActions>
        )}
      </Card>
      <LawyerAgreementDialog
        isOpen={dialogOpen}
        setIsOpen={setDialogOpen}
        onAccept={onAgreementAccept}
      />
    </form>
  );
};
